import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getCompleteProducts, getCategories, getSubCategories } from "../api";
import SortDropdown from "./SortDropdown";
import FilterSidebar from "./FilterSidebar";
import "./ProductList.css";
import { useCart } from "../../Context/CartContext";
import { useWishlist } from "../../Context/WishlistContext";
import { useCurrency } from "../../Context/CurrencyContext";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

// ===== IMPORTS FOR THE THREE SECTIONS =====
import HomeNewsletterSection from "../Home/HomeNewsletterSection";
import HomeFeatures from "../Home/HomeFeatures";
import HomeCategorySection from "../Home/HomeCategorySection";
// ==========================================

const BASE_URL = "https://volumaire.backendapihub.com";

const ProductList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart, openCart } = useCart();
  const { addToWishlist, isInWishlist } = useWishlist();
  const { formatPrice } = useCurrency();
  const [openFilter, setOpenFilter] = useState(false);
  const [sortBy, setSortBy] = useState("featured");
  const [filters, setFilters] = useState({
    brands: [],
    types: [],
    availability: [],
  });
  const [appliedFilters, setAppliedFilters] = useState({
    brands: [],
    types: [],
    availability: [],
  });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalProducts: 0,
  });
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const hasFetched = useRef(false);

  // State for category and subcategory data with banners
  const [currentCategory, setCurrentCategory] = useState(null);
  const [currentSubcategory, setCurrentSubcategory] = useState(null);
  const [categoryData, setCategoryData] = useState([]);
  const [subcategoryData, setSubcategoryData] = useState([]);
  const [loadingCategoryInfo, setLoadingCategoryInfo] = useState(true);

  // Parse URL parameters
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const categoryParam = searchParams.get('category');
  const subcategoryParam = searchParams.get('subcategory');

  // Default image path in public folder
  const DEFAULT_IMAGE = "/images/dailyroutine2.webp";

  // Color map for category banners
  const categoryColorMap = {
    'face care': '#F4ECE2',
    'face': '#F4ECE2',
    'bath & body': '#D2E8F4',
    'bath': '#D2E8F4',
    'body': '#D2E8F4',
    'hair care': '#F3EBDF',
    'hair': '#F3EBDF',
    'essential oils': '#F9F1D6',
    'essential': '#F9F1D6',
    'oils': '#F9F1D6'
  };

  const normalizeImageUrl = (imageUrl) => {
    if (!imageUrl || imageUrl.trim() === "" || imageUrl === "null" || imageUrl === "undefined") {
      return null;
    }
    
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('data:')) {
      return imageUrl;
    }
    
    if (imageUrl.startsWith('/')) {
      return `${BASE_URL}${imageUrl}`;
    }
    
    return `${BASE_URL}/${imageUrl}`;
  };

  const isValidImageUrl = (url) => {
    if (!url) return false;
    const strUrl = String(url).trim();
    return strUrl !== "" && 
          strUrl !== "null" && 
          strUrl !== "undefined" && 
          !strUrl.includes('undefined') &&
          !strUrl.includes('null');
  };

  // ===== UPDATED: Get product image from various possible fields =====
  const getProductImage = (product, isHover = false) => {
    try {
      // Try hover image first if requested
      if (isHover) {
        // Check for hover-specific fields (img2, hoverImage, etc.)
        const hoverImage = product.img2 || product.hoverImage || product.images?.[1];
        if (hoverImage && isValidImageUrl(hoverImage)) {
          return normalizeImageUrl(hoverImage) || DEFAULT_IMAGE;
        }
      }

      // Main image: try multiple possible fields
      const mainImage = product.img1 || product.image || product.mainImage || 
                        (product.images && product.images[0]) || 
                        product.imageUrl || product.thumbnail;
      
      if (mainImage && isValidImageUrl(mainImage)) {
        return normalizeImageUrl(mainImage);
      }
      
      return DEFAULT_IMAGE;
    } catch (error) {
      console.error("Error in getProductImage:", error);
      return DEFAULT_IMAGE;
    }
  };
  // ===================================================================

  // Get banner background color based on URL param first, then category data
  const getBannerBackgroundColor = () => {
    const param = subcategoryParam || categoryParam;
    if (param) {
      const paramLower = param.toLowerCase().replace(/-/g, ' ');
      if (categoryColorMap[paramLower]) {
        return categoryColorMap[paramLower];
      }
      for (const [key, color] of Object.entries(categoryColorMap)) {
        if (paramLower.includes(key)) {
          return color;
        }
      }
    }

    const name = (currentSubcategory?.name || currentCategory?.name || '').toLowerCase().trim();
    if (categoryColorMap[name]) {
      return categoryColorMap[name];
    }
    for (const [key, color] of Object.entries(categoryColorMap)) {
      if (name.includes(key)) {
        return color;
      }
    }
    
    return '#f5f5f5';
  };

  // Fetch category and subcategory data with banners
  const fetchCategoryData = useCallback(async () => {
    setLoadingCategoryInfo(true);
    try {
      const [categoriesRes, subcategoriesRes] = await Promise.all([
        getCategories(),
        getSubCategories()
      ]);

      let categories = [];
      if (Array.isArray(categoriesRes)) categories = categoriesRes;
      else if (categoriesRes?.data && Array.isArray(categoriesRes.data)) categories = categoriesRes.data;
      else if (categoriesRes?.categories && Array.isArray(categoriesRes.categories)) categories = categoriesRes.categories;

      const processedCategories = categories.map(cat => ({
        id: cat.id || cat._id,
        name: cat.name || cat.categoryName || cat.title,
        slug: cat.slug || (cat.name || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
        banner: cat.banner || cat.bannerImage || cat.image || cat.coverImage || null,
        description: cat.description || '',
        image: cat.image || cat.coverImage || null
      }));

      setCategoryData(processedCategories);

      let subcategories = [];
      if (Array.isArray(subcategoriesRes)) subcategories = subcategoriesRes;
      else if (subcategoriesRes?.data && Array.isArray(subcategoriesRes.data)) subcategories = subcategoriesRes.data;
      else if (subcategoriesRes?.subcategories && Array.isArray(subcategoriesRes.subcategories)) subcategories = subcategoriesRes.subcategories;

      const processedSubcategories = subcategories.map(sub => ({
        id: sub.id || sub._id,
        name: sub.name || sub.subcategoryName || sub.title,
        slug: sub.slug || (sub.name || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
        categoryId: sub.categoryId || sub.parentId || sub.parentCategoryId,
        banner: sub.banner || sub.bannerImage || sub.image || sub.coverImage || null,
        description: sub.description || '',
        image: sub.image || sub.coverImage || null
      }));

      setSubcategoryData(processedSubcategories);

      if (categoryParam) {
        const matchedCategory = processedCategories.find(c => 
          c.slug === categoryParam || 
          c.name?.toLowerCase().replace(/\s+/g, '-') === categoryParam
        );
        if (matchedCategory) setCurrentCategory(matchedCategory);
      }

      if (subcategoryParam) {
        const matchedSubcategory = processedSubcategories.find(s => 
          s.slug === subcategoryParam ||
          s.name?.toLowerCase().replace(/\s+/g, '-') === subcategoryParam
        );
        if (matchedSubcategory) {
          setCurrentSubcategory(matchedSubcategory);
          if (matchedSubcategory.categoryId) {
            const parentCategory = processedCategories.find(c => c.id === matchedSubcategory.categoryId);
            if (parentCategory) setCurrentCategory(parentCategory);
          }
        }
      }

    } catch (error) {
      console.error("Error fetching category data:", error);
    } finally {
      setLoadingCategoryInfo(false);
    }
  }, [categoryParam, subcategoryParam]);

  // ===== FIXED FETCH PRODUCTS FOR THE ACTUAL API RESPONSE =====
  const fetchProducts = useCallback(async (page = 1, limit = 10) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit };
      if (categoryParam) params.category = categoryParam;
      if (subcategoryParam) params.subcategory = subcategoryParam;

      const response = await getCompleteProducts(params);

      let productsData = [];
      let total = 0;
      let totalPages = 1;

      if (response && response.data) {
        if (Array.isArray(response.data)) {
          productsData = response.data;
          total = productsData.length;
          totalPages = Math.ceil(total / limit);
        } else if (response.data.products && Array.isArray(response.data.products)) {
          productsData = response.data.products;
          if (response.data.pagination) {
            total = response.data.pagination.total || productsData.length;
            totalPages = response.data.pagination.totalPages || Math.ceil(total / limit);
          } else {
            total = productsData.length;
            totalPages = Math.ceil(total / limit);
          }
        } else {
          productsData = [];
        }
      } else if (Array.isArray(response)) {
        productsData = response;
        total = productsData.length;
        totalPages = Math.ceil(total / limit);
      } else if (response && response.products) {
        productsData = Array.isArray(response.products) ? response.products : [];
        total = response.total || response.totalProducts || productsData.length;
        totalPages = response.totalPages || Math.ceil(total / limit) || 1;
      }

      setProducts(productsData);
      setPagination({
        page,
        limit,
        totalPages,
        totalProducts: total,
      });
    } catch (err) {
      console.error("Error fetching products:", err);
      setError(err.message || "Failed to load products. Please try again.");
    } finally {
      setLoading(false);
      hasFetched.current = true;
    }
  }, [categoryParam, subcategoryParam]);
  // ============================================================

  // Fetch category data on mount and when params change
  useEffect(() => {
    fetchCategoryData();
  }, [categoryParam, subcategoryParam, fetchCategoryData]);

  useEffect(() => {
    hasFetched.current = false;
  }, [categoryParam, subcategoryParam]);

  useEffect(() => {
    if (!loadingCategoryInfo && !hasFetched.current) {
      fetchProducts();
    }
    return () => { hasFetched.current = false; };
  }, [fetchProducts, loadingCategoryInfo]);

  // ===== PRODUCT COUNTS WITH ARRAY GUARD =====
  const productCounts = useMemo(() => {
    if (!Array.isArray(products)) {
      return {
        brands: {},
        types: {},
        availability: { inStock: 0, outOfStock: 0 },
      };
    }

    const brandCounts = {};
    const typeCounts = {};
    let inStockCount = 0;
    let outOfStockCount = 0;

    products.forEach(product => {
      const brand = product.brand || "Unknown";
      brandCounts[brand] = (brandCounts[brand] || 0) + 1;

      const type = product.type || product.category || "Uncategorized";
      typeCounts[type] = (typeCounts[type] || 0) + 1;

      if (product.inStock) {
        inStockCount++;
      } else {
        outOfStockCount++;
      }
    });

    return {
      brands: brandCounts,
      types: typeCounts,
      availability: { inStock: inStockCount, outOfStock: outOfStockCount },
    };
  }, [products]);
  // ============================================

  const filteredAndSortedProducts = useMemo(() => {
    if (!Array.isArray(products)) return [];
    let filtered = [...products];

    if (appliedFilters.brands.length > 0) {
      filtered = filtered.filter(p => appliedFilters.brands.includes(p.brand || "Unknown"));
    }

    if (appliedFilters.types.length > 0) {
      filtered = filtered.filter(p => {
        const type = p.type || p.category || "Uncategorized";
        return appliedFilters.types.includes(type);
      });
    }

    if (appliedFilters.availability.length > 0) {
      filtered = filtered.filter(p => {
        if (appliedFilters.availability.includes("inStock") && p.inStock) return true;
        if (appliedFilters.availability.includes("outOfStock") && !p.inStock) return true;
        return false;
      });
    }

    switch (sortBy) {
      case "price-low":
        filtered.sort((a, b) => (a.price || a.priceValue || 0) - (b.price || b.priceValue || 0));
        break;
      case "price-high":
        filtered.sort((a, b) => (b.price || b.priceValue || 0) - (a.price || a.priceValue || 0));
        break;
      case "newest":
        filtered.sort((a, b) => (b.createdAt || b.id) - (a.createdAt || a.id));
        break;
      case "name-asc":
        filtered.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        break;
      case "name-desc":
        filtered.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
        break;
      default: // "featured"
        break;
    }

    return filtered;
  }, [products, appliedFilters, sortBy]);

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setOpenFilter(false);
  };

  const handleResetFilters = () => {
    const resetFilters = { brands: [], types: [], availability: [] };
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
  };

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  const handleAddToCart = (product, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    const cartItem = {
      id: product.id,
      name: product.name || product.title,
      title: product.name || product.title,
      price: product.price || product.priceValue || 0,
      priceValue: product.priceValue || product.price || 0,
      selectedSize: "default",
      selectedFragrance: "default",
      image: getProductImage(product), // Use our new function
      brand: product.brand || "Unknown",
      inStock: product.inStock || true,
      qty: 1,
      uniqueId: `${product.id}-default-default`,
      basePrice: product.priceValue || product.price || 0,
      baseCurrency: 'USD'
    };
    
    addToCart(cartItem, 1);
    openCart();
  };

  const handleWishlistToggle = (product, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    const wishlistProduct = {
      id: product.id,
      title: product.name || product.title,
      price: product.price || product.priceValue || 0,
      image: getProductImage(product),
      brand: product.brand || "Unknown",
      inStock: product.inStock || true
    };
    
    addToWishlist(wishlistProduct);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchProducts(newPage, pagination.limit);
    }
  };

  // ========== FIXED: Banner image now uses ONLY category/subcategory banners ==========
  const getBannerImage = () => {
    // Prioritize subcategory banner, then category banner
    if (currentSubcategory?.banner) {
      return normalizeImageUrl(currentSubcategory.banner);
    }
    if (currentCategory?.banner) {
      return normalizeImageUrl(currentCategory.banner);
    }
    return null;
  };
  // ============================================================================

  const getPageDescription = () => {
    if (currentSubcategory?.description) return currentSubcategory.description;
    if (currentCategory?.description) return currentCategory.description;
    if (categoryParam || subcategoryParam) {
      return `Explore our collection of ${categoryParam || subcategoryParam}.`;
    }
    return "Explore pure, natural essential oils, essential oil blends, vegan, chemical-free, synthetic-free.";
  };

  const getPageTitle = () => {
    if (subcategoryParam && currentSubcategory) return currentSubcategory.name;
    if (categoryParam && currentCategory) return currentCategory.name;
    if (categoryParam) {
      return categoryParam.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
    if (subcategoryParam) {
      return subcategoryParam.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
    return "Essential oils";
  };

  const bannerImage = getBannerImage();
  const pageDescription = getPageDescription();
  const showBanner = !!(categoryParam || subcategoryParam);
  const bannerBgColor = getBannerBackgroundColor();

  if (loading && products.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading {getPageTitle()}...</p>
        <p>Please wait while we fetch the latest products.</p>
      </div>
    );
  }

  if (error && products.length === 0) {
    return (
      <div className="error-container">
        <p className="error-message">⚠️ {error}</p>
        <p className="api-info">API Endpoint: <code>{BASE_URL}/api/complete-products</code></p>
        <button className="retry-button" onClick={() => fetchProducts()}>Retry Loading Products</button>
        <div className="debug-info">
          <p>Debug Information:</p>
          <button className="debug-button" onClick={() => {
            console.log("=== DEBUG ===");
            console.log("API Base URL:", BASE_URL);
            console.log("Category filter:", categoryParam);
            console.log("Subcategory filter:", subcategoryParam);
            console.log("Current Category:", currentCategory);
            console.log("Current Subcategory:", currentSubcategory);
            console.log("Products state:", products);
            console.log("Pagination:", pagination);
          }}>Show Debug Info in Console</button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-list-container">
      {/* Add custom styles for pagination buttons and product details alignment & spacing */}
      <style>
        {`
          .pagination-btn {
            background: none !important;
            border: none !important;
            color: #000 !important;
            box-shadow: none !important;
            outline: none !important;
            cursor: pointer;
            font-size: 1.2rem;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0 8px;
          }
          .pagination-btn:hover {
            opacity: 0.7;
          }
          .pagination-btn:disabled {
            opacity: 0.3;
            cursor: not-allowed;
          }
          .pagination-btn svg {
            fill: currentColor;
            stroke: currentColor;
          }
          .page-info {
            color: #000;
            font-size: 0.9rem;
            margin: 0 10px;
          }

          /* Fix alignment and spacing for product details */
          .product-details-custom {
            text-align: left !important;
            display: block !important;
          }
          .product-brand-custom,
          .product-name-custom,
          .product-price-custom {
            text-align: left !important;
            margin: 0 !important;
            padding: 0 !important;
            line-height: 1.3 !important;
          }
          .product-name-custom {
            margin-bottom: 2px !important; /* minimal gap below name */
          }
          .product-price-custom {
            margin-top: 0 !important; /* no top margin */
          }
          /* Ensure container doesn't add extra spacing */
          .product-card-custom .product-details-custom {
            padding: 8px 0 0 0 !important;
          }
        `}
      </style>

      {/* Category/Subcategory Banner */}
      {showBanner && (
        <div className="category-banner" style={{ backgroundColor: bannerBgColor }}>
          <div className="banner-container">
            <div className="banner-content">
              <h1 className="banner-title">{getPageTitle()}</h1>
              {pageDescription && <p className="banner-description">{pageDescription}</p>}
            </div>
            <div className="banner-image" style={bannerImage ? { backgroundImage: `url(${bannerImage})` } : {}} />
          </div>
        </div>
      )}

      <div className="product-wrapper">
        <div className="product-header">
          <div className="header-left">
            <div className="product-stats">
              <p className="product-count"><strong>{pagination.totalProducts}</strong> products</p>
              {(appliedFilters.brands.length > 0 || appliedFilters.types.length > 0 || appliedFilters.availability.length > 0) && (
                <div className="applied-filters">
                  <span>Active Filters: </span>
                  {appliedFilters.brands.map(brand => <span key={brand} className="filter-tag">Brand: {brand}</span>)}
                  {appliedFilters.types.map(type => <span key={type} className="filter-tag">Type: {type}</span>)}
                  {appliedFilters.availability.map(avail => (
                    <span key={avail} className="filter-tag">
                      {avail === "inStock" ? "In Stock" : "Out of Stock"}
                    </span>
                  ))}
                  <button onClick={handleResetFilters} className="clear-filters-btn">Clear All</button>
                </div>
              )}
            </div>
          </div>
          <div className="header-right">
            <div className="product-actions">
              <SortDropdown sortBy={sortBy} onSortChange={setSortBy} />
              <span className="filter-trigger" onClick={() => setOpenFilter(true)}>
                <span>☰</span> Filter Products
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="warning-banner">
            <p>⚠️ {error}</p>
            <small>Some features may be limited</small>
          </div>
        )}

        {filteredAndSortedProducts.length === 0 ? (
          <div className="no-products">
            <h3>No products found</h3>
            <p>Try adjusting your filters or search criteria</p>
            <button onClick={handleResetFilters} className="reset-filters-btn">Reset All Filters</button>
            <button onClick={() => fetchProducts()} className="retory-button">Refresh Products</button>
          </div>
        ) : (
          <>
            <div className="product-grid">
              {filteredAndSortedProducts.map((product) => {
                const mainImage = getProductImage(product);
                const hoverImage = getProductImage(product, true);
                
                return (
                  <div 
                    key={product.id} 
                    className="product-card-wrapper"
                    onMouseEnter={() => setHoveredProductId(product.id)}
                    onMouseLeave={() => setHoveredProductId(null)}
                  >
                    <div className="product-card-custom">
                      <div className="product-image-container-custom" onClick={() => handleProductClick(product.id)}>
                        <button 
                          className={`wishlist-btn-productlist ${isInWishlist(product.id) ? 'active' : ''}`}
                          onClick={(e) => handleWishlistToggle(product, e)}
                          title={isInWishlist(product.id) ? "Remove from wishlist" : "Add to wishlist"}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill={isInWishlist(product.id) ? "red" : "none"} stroke="currentColor" strokeWidth="2">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                          </svg>
                        </button>

                        <img 
                          src={hoveredProductId === product.id && hoverImage !== DEFAULT_IMAGE ? hoverImage : mainImage} 
                          alt={product.name || product.title} 
                          className="product-img-custom" 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = DEFAULT_IMAGE;
                          }}
                        />
                        <button className="add-to-cart-btn-custom" onClick={(e) => handleAddToCart(product, e)}>
                          ADD TO CART
                        </button>
                      </div>

                      <div className="product-details-custom" onClick={() => handleProductClick(product.id)}>
                        <p className="product-brand-custom">{product.brand || "Unknown Brand"}</p>
                        <p className="product-name-custom">{product.name || product.title}</p>
                        <p className="product-price-custom">
                          {formatPrice(product.price || product.priceValue || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="pagination-btn"
                  aria-label="Previous page"
                >
                  <FaChevronLeft />
                </button>
                <span className="page-info">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button 
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="pagination-btn"
                  aria-label="Next page"
                >
                  <FaChevronRight />
                </button>
              </div>
            )}
          </>
        )}

        <FilterSidebar
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          filters={filters}
          setFilters={setFilters}
          onApply={handleApplyFilters}
          productCounts={productCounts}
          onReset={handleResetFilters}
        />
      </div>

      {/* Three bottom sections */}
      <div className="product-list-bottom-sections">
        <HomeCategorySection />
        <HomeFeatures />
        <HomeNewsletterSection />
      </div>
    </div>
  );
};

export default ProductList;